import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';

export default function CreateDish({ showNotification }) {
  const { t } = useLang();
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]); // [{name, nameZh}]
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryZh, setNewCategoryZh] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);

  // Variants: [{label, labelZh, price}]
  const [variants, setVariants] = useState([]);
  const [useVariants, setUseVariants] = useState(false);

  const dishImage = watch('dishImage');
  const selectedCategory = watch('category');

  useEffect(() => {
    if (dishImage && dishImage[0]) {
      const reader = new FileReader();
      reader.onload = e => setImagePreview(e.target.result);
      reader.readAsDataURL(dishImage[0]);
    } else {
      setImagePreview(null);
    }
  }, [dishImage]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/dishes/categories`)
      .then(res => { if (res.data.success) setCategories(res.data.data); })
      .catch(() => {});
  }, []);

  // Auto-fill Chinese category when picking an existing English category
  useEffect(() => {
    if (showNewCat || !selectedCategory) return;
    const found = categories.find(c => c.name === selectedCategory);
    if (found) setValue('categoryZh', found.nameZh || '');
  }, [selectedCategory, categories, showNewCat, setValue]);

  const addVariant = () => {
    setVariants(prev => [...prev, { label: '', labelZh: '', price: '' }]);
  };
  const updateVariant = (idx, key, val) => {
    setVariants(prev => prev.map((v, i) => i === idx ? { ...v, [key]: val } : v));
  };
  const removeVariant = (idx) => {
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleUseVariants = () => {
    setUseVariants(prev => {
      const next = !prev;
      if (next && variants.length === 0) {
        setVariants([{ label: '', labelZh: '', price: '' }, { label: '', labelZh: '', price: '' }]);
      }
      return next;
    });
  };

  const onSubmit = async (formData) => {
    // Validate variants if enabled
    if (useVariants) {
      const cleanVariants = variants.filter(v => v.label.trim() && v.price);
      if (cleanVariants.length < 2) {
        showNotification('Please add at least 2 variants with labels and prices, or disable variants.', 'error');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('ownerToken');
      const data = new FormData();
      if (formData.dishImage?.[0]) data.append('dishImage', formData.dishImage[0]);
      data.append('name', formData.name.trim());
      if (formData.nameZh && formData.nameZh.trim()) data.append('nameZh', formData.nameZh.trim());
      if (formData.dishNumber && formData.dishNumber.trim()) data.append('dishNumber', formData.dishNumber.trim());

      // When using variants, price field is ignored server-side (server uses min variant price)
      data.append('price', useVariants ? '0.01' : parseFloat(formData.price).toFixed(2));

      const cat = showNewCat ? newCategory.trim() : formData.category;
      const catZh = showNewCat ? newCategoryZh.trim() : (formData.categoryZh || '').trim();
      if (!cat) { showNotification('Category is required', 'error'); setLoading(false); return; }
      data.append('category', cat);
      if (catZh) data.append('categoryZh', catZh);

      data.append('description', formData.description.trim());
      data.append('isPopular', formData.isPopular || false);
      if (formData.discountPrice && !useVariants) data.append('discountPrice', parseFloat(formData.discountPrice).toFixed(2));

      if (useVariants) {
        const cleanVariants = variants
          .filter(v => v.label.trim() && v.price)
          .map(v => ({ label: v.label.trim(), labelZh: v.labelZh.trim() || null, price: parseFloat(v.price) }));
        data.append('variants', JSON.stringify(cleanVariants));
      }

      const res = await axios.post(`${API_BASE_URL}/dishes/create-dish`, data, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        showNotification('Dish created successfully!', 'success');
        reset();
        setNewCategory('');
        setNewCategoryZh('');
        setShowNewCat(false);
        setVariants([]);
        setUseVariants(false);
        setTimeout(() => navigate('/dishes'), 1200);
      } else {
        showNotification(`Failed: ${res.data.message}`, 'error');
      }
    } catch {
      showNotification('Failed to create dish. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="dish-form-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-plus-square me-2"></i>{t('dishes.addTitle')}</h2>
          <p className="page-subtitle">{t('dishes.addDishSub')}</p>
        </div>
        <button onClick={() => navigate('/dishes')} className="btn-secondary-action">
          <i className="bi bi-arrow-left me-1"></i>{t('common.back')}
        </button>
      </div>

      <div className="dish-form-card">
        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <div className="form-grid-2">
            {/* Left column */}
            <div>
              {/* Image Upload — now optional */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishImage')} <small style={{ color: '#9B8579', fontWeight: 500 }}>{t('dishes.optional')}</small></label>
                <label className="image-drop-zone">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-drop-preview" />
                  ) : (
                    <div className="image-drop-placeholder">
                      <i className="bi bi-cloud-upload"></i>
                      <span>{t('dishes.uploadHint')}</span>
                      <small>{t('dishes.skipHint')}</small>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpg,image/jpeg,image/png,image/webp"
                    style={{ display: 'none' }}
                    {...register('dishImage', {
                      validate: files => !files?.[0] || files[0].size / 1024 / 1024 <= 5 || 'Max 5MB'
                    })}
                  />
                </label>
                {errors.dishImage && <p className="form-error">{errors.dishImage.message}</p>}
              </div>

              {/* English Name */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNameEn')} <span className="required">*</span></label>
                <input
                  type="text"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  placeholder="e.g. Chicken Seekh Kabab"
                  {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Min 2 chars' } })}
                />
                {errors.name && <p className="form-error">{errors.name.message}</p>}
              </div>

              {/* Chinese Name */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNameZh')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 串烤鸡肉卷"
                  {...register('nameZh')}
                />
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  {t('dishes.shownMiniZh')}
                </small>
              </div>

              {/* Dish / Item Number — admin + receipt only, never shown in the mini program */}
              <div className="form-group">
                <label className="form-label">{t('dishes.dishNumber')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 97A, 21B"
                  maxLength={20}
                  {...register('dishNumber')}
                />
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  {t('dishes.dishNumberHint')}
                </small>
              </div>

              {/* Variants toggle */}
              <div className="form-group" style={{ background: '#FFF8E7', border: '2px solid #E8D5A8', borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label className="form-label mb-0" style={{ margin: 0 }}>
                    <i className="bi bi-rulers me-1" style={{ color: '#a05a2c' }}></i>
                    {t('dishes.multiSize')}
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={useVariants}
                      onChange={toggleUseVariants}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#6B5347' }}>
                      {useVariants ? t('dishes.enabled') : t('dishes.useOnePrice')}
                    </span>
                  </label>
                </div>
                <small style={{ color: '#8B4A24', fontSize: 12, lineHeight: 1.5, display: 'block' }}>
                  Enable for dishes like <b>"2pcs / 4pcs"</b> or <b>"Small / Big"</b> where the customer must choose.
                  The base price field below is ignored when variants are on — the customer picks one.
                </small>
              </div>

              {!useVariants && (
                /* Price + Discount Price */
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('dishes.price')} <span className="required">*</span></label>
                    <input
                      type="number" step="0.01" min="0.01"
                      className={`form-input ${errors.price ? 'error' : ''}`}
                      placeholder="0.00"
                      {...register('price', {
                        required: !useVariants && 'Price is required',
                        min: { value: 0.01, message: 'Must be > 0' }
                      })}
                    />
                    {errors.price && <p className="form-error">{errors.price.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('dishes.discountPrice')}</label>
                    <input
                      type="number" step="0.01" min="0"
                      className="form-input"
                      placeholder="Optional"
                      {...register('discountPrice')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right column */}
            <div>
              {/* Category English */}
              <div className="form-group">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">{t('dishes.categoryEn')} <span className="required">*</span></label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ fontSize: '12px', color: 'var(--primary)', background: 'none', border: 'none', padding: 0 }}
                    onClick={() => setShowNewCat(v => !v)}
                  >
                    {showNewCat ? t('dishes.useExisting') : t('dishes.newCategory')}
                  </button>
                </div>

                {showNewCat ? (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('dishes.typeNewCat')}
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                  />
                ) : (
                  <select
                    className={`form-select-input ${errors.category ? 'error' : ''}`}
                    {...register('category', { required: !showNewCat })}
                  >
                    <option value="">{t('dishes.selectCategory')}</option>
                    {categories.map(c => (
                      <option key={c.name} value={c.name}>
                        {c.nameZh ? `${c.name} / ${c.nameZh}` : c.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.category && !showNewCat && <p className="form-error">Category is required</p>}
              </div>

              {/* Category Chinese */}
              <div className="form-group">
                <label className="form-label">{t('dishes.categoryZh')}</label>
                {showNewCat ? (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. 主菜"
                    value={newCategoryZh}
                    onChange={e => setNewCategoryZh(e.target.value)}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('dishes.autoFilled')}
                    {...register('categoryZh')}
                  />
                )}
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  {t('dishes.shownSidebarZh')}
                </small>
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">{t('dishes.description')} <span className="required">*</span></label>
                <textarea
                  rows="4"
                  className={`form-textarea ${errors.description ? 'error' : ''}`}
                  placeholder={t('dishes.describeDish')}
                  {...register('description', {
                    required: 'Description is required',
                    minLength: { value: 10, message: 'Min 10 chars' }
                  })}
                ></textarea>
                {errors.description && <p className="form-error">{errors.description.message}</p>}
              </div>

              {/* Popular */}
              <div className="form-group form-check-row">
                <input type="checkbox" id="isPopular" className="form-check-input" {...register('isPopular')} />
                <label htmlFor="isPopular" className="form-check-label">
                  <i className="bi bi-star me-1"></i>{t('dishes.markPopular')}
                </label>
              </div>
            </div>
          </div>

          {/* Variants list — full width below the grid */}
          <AnimatePresence>
            {useVariants && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <VariantEditor variants={variants} onUpdate={updateVariant} onRemove={removeVariant} onAdd={addVariant} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="form-actions">
            <button type="button" onClick={() => navigate('/dishes')} className="btn-cancel-form" disabled={loading}>
              <i className="bi bi-x-circle me-2"></i>{t('common.cancel')}
            </button>
            <button type="submit" className="btn-submit-form" disabled={loading}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</>
              ) : (
                <><i className="bi bi-save me-2"></i>{t('dishes.saveDish')}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}

// ── Variant editor (shared between Create + Edit) ──────────────────────────
export function VariantEditor({ variants, onUpdate, onRemove, onAdd }) {
  return (
    <div className="variant-editor">
      <div className="variant-editor-header">
        <h4 className="variant-editor-title">
          <i className="bi bi-list-ol me-2"></i>
          Variants ({variants.length})
        </h4>
        <small style={{ color: '#9B8579' }}>Each variant becomes a choice the customer picks before adding to cart.</small>
      </div>

      <div className="variant-list">
        {variants.map((v, i) => (
          <div className="variant-row" key={i}>
            <div className="variant-row-num">{i + 1}</div>
            <div className="variant-row-fields">
              <input
                className="form-input"
                placeholder="Label (e.g. 2pcs, Small)"
                value={v.label}
                onChange={e => onUpdate(i, 'label', e.target.value)}
              />
              <input
                className="form-input"
                placeholder="中文 (e.g. 2串, 小)"
                value={v.labelZh}
                onChange={e => onUpdate(i, 'labelZh', e.target.value)}
              />
              <input
                className="form-input"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="Price (¥)"
                value={v.price}
                onChange={e => onUpdate(i, 'price', e.target.value)}
              />
            </div>
            <button type="button" className="variant-remove-btn" onClick={() => onRemove(i)} title="Remove variant">
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ))}
      </div>

      <button type="button" className="variant-add-btn" onClick={onAdd}>
        <i className="bi bi-plus-circle me-2"></i>Add another variant
      </button>
    </div>
  );
}
